import { Button, ButtonProps } from "@src/components/ui-components/Buttons/Button/Button";
import { t } from "i18next";
import { ReactNode } from "react";
import { Popup } from "../Popup/Popup";
import { PopupContainer, PopupContainerStylingProps } from "../PopupContainer/PopupContainer";
import styles from "./ModalPopup.module.css";

export type ActionButton = {
  type: ButtonProps["type"];
  title: string;
  onClick: () => void;
  backgroundColor?: string;
  disabled?: boolean;
};

export type ModalPopupProps = {
  id: string;
  title?: string;
  onActionClicked?: () => void;
  children?: ReactNode;

  actionTitle?: string;
  actionButtonType?: ButtonProps["type"];
  disableAction?: boolean;

  actionButtons?: ActionButton[];
  extraActionButtons?: ActionButton[];
  closable?: boolean;
  containerProps?: PopupContainerStylingProps;

  maxHeight?: string;
  topMargin?: string;
};

export const ModalPopup = ({
  id,
  actionButtons,
  onActionClicked,
  actionTitle,
  actionButtonType,
  disableAction,
  extraActionButtons,
  title,
  closable,
  containerProps,
  children,
  topMargin,
  maxHeight,
}: ModalPopupProps) => {
  const runAction = (action?: () => void) => {
    Popup.remove(id);
    action?.();
  };

  const actions: ActionButton[] = actionButtons ?? [
    { title: t("main.cancel"), type: "secondary", onClick: () => runAction() },
    {
      onClick: () => runAction(onActionClicked),
      title: actionTitle ?? t("main.confirm"),
      type: actionButtonType ?? "primary",
      disabled: disableAction,
    },
  ];

  if (extraActionButtons) actions.splice(1, 0, ...extraActionButtons);

  return (
    <PopupContainer
      id={id}
      title={title}
      closable={closable}
      className={styles.container}
      horizontalCenter
      minWidth="300px"
      topMargin={topMargin ?? "15vh"}
      maxHeight={maxHeight ?? "65vh"}
      {...containerProps}
    >
      <div>
        <div className={styles.content}>{children}</div>
        {actions.length > 0 && (
          <div className={styles.actionsButtons}>
            {actions.map((button) => (
              <Button
                type={button.type}
                disabled={button.disabled}
                onClick={button.onClick}
                key={button.title}
                data-cy={`popup-button-${button.title.toLowerCase()}`}
              >
                {button.title}
              </Button>
            ))}
          </div>
        )}
      </div>
    </PopupContainer>
  );
};
